// src/components/WarteschlangeAnzeige.jsx
//
// Sichtbarer Stand der lokalen Eintrags-Warteschlange (utils/eintragsWarteschlange.js).
// Erscheint nur, wenn etwas nicht gesendet ist – dann aber auf jeder Seite,
// damit ein Betreuer das Geraet nicht mit ungesendeten Ergebnissen weglegt.
import { useEffect, useState } from "react";
import { CloudOff, AlertTriangle, RefreshCw } from "lucide-react";
import Modal from "./ui/Modal";
import Button from "./ui/Button";
import {
  warteschlange,
  abonnieren,
  alleSenden,
  entfernen,
  trotzdemSenden,
} from "../utils/eintragsWarteschlange";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function wertText(payload, info) {
  const wert = payload.estimate_value ?? payload.performance_score;
  if (wert === undefined || wert === null) return "–";
  return `${String(wert).replace(".", ",")}${info?.unit ? " " + info.unit : ""}`;
}

function serverWertText(eintrag, info) {
  if (!eintrag) return "–";
  const wert = eintrag.estimate_value ?? eintrag.performance_score;
  const zeit = eintrag.timestamp
    ? new Date(eintrag.timestamp).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })
    : "";
  return `${String(wert).replace(".", ",")}${info?.unit ? " " + info.unit : ""}` +
    `${eintrag.entry_by ? ` von ${eintrag.entry_by}` : ""}${zeit ? ` um ${zeit}` : ""}`;
}

export default function WarteschlangeAnzeige() {
  const [liste, setListe] = useState(() => warteschlange());
  const [offen, setOffen] = useState(false);
  const [sendet, setSendet] = useState(false);

  useEffect(() => abonnieren(setListe), []);

  if (liste.length === 0) return null;

  const wartend = liste.filter((e) => e.status === "wartet").length;
  const probleme = liste.length - wartend;

  const jetztSenden = async () => {
    setSendet(true);
    try {
      await alleSenden(BACKEND_URL);
    } finally {
      setSendet(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOffen(true)}
        className={`fixed bottom-4 right-4 z-40 flex items-center gap-2 px-4 py-3 rounded-full shadow-lg font-semibold text-sm ${
          probleme > 0 ? "bg-red-600 text-white" : "bg-yellow-400 text-black"
        }`}
      >
        {probleme > 0 ? <AlertTriangle className="w-5 h-5" /> : <CloudOff className="w-5 h-5" />}
        {probleme > 0
          ? `${probleme} ${probleme === 1 ? "Ergebnis braucht" : "Ergebnisse brauchen"} Klärung`
          : `${wartend} ${wartend === 1 ? "Ergebnis" : "Ergebnisse"} nicht gesendet`}
      </button>

      <Modal
        open={offen}
        onClose={() => setOffen(false)}
        title="Nicht gesendete Ergebnisse"
        footer={
          <>
            <Button variant="secondary" onClick={() => setOffen(false)}>
              Schließen
            </Button>
            <Button onClick={jetztSenden} disabled={sendet || wartend === 0}>
              <RefreshCw className={`w-4 h-4 mr-2 ${sendet ? "animate-spin" : ""}`} />
              Jetzt senden
            </Button>
          </>
        }
      >
        <p className="text-sm text-gray-400">
          Diese Ergebnisse sind nur auf diesem Gerät gespeichert. Wartende werden automatisch
          gesendet, sobald wieder eine Verbindung besteht – das Gerät bitte bis dahin nicht
          zurücksetzen und die Seite nicht im privaten Modus schließen.
        </p>
        <ul className="space-y-3">
          {liste.map((e) => (
            <li key={e.id} className="p-3 rounded-lg border border-secondary-700 bg-secondary-800 text-sm">
              <div className="font-semibold">
                {e.info?.team_name || e.payload.team_id} – Station {e.payload.station_number}
                {e.info?.station_name ? ` (${e.info.station_name})` : ""}
              </div>
              <div className="text-gray-300">
                Wert: {wertText(e.payload, e.info)} · von {e.payload.entry_by}
              </div>

              {e.status === "wartet" && (
                <div className="text-yellow-300 text-xs mt-1">
                  Wartet auf Verbindung{e.versuche > 0 ? ` (${e.versuche} Versuche)` : ""}
                  {e.letzterFehler ? ` – ${e.letzterFehler}` : ""}
                </div>
              )}

              {e.status === "konflikt" && (
                <div className="mt-2 space-y-2">
                  <div className="text-red-300 text-xs">
                    Inzwischen wurde auf einem anderen Gerät ein anderer Wert eingetragen:{" "}
                    <strong>{serverWertText(e.serverEintrag, e.info)}</strong>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="danger" className="text-xs px-3 py-1" onClick={() => trotzdemSenden(e.id)}>
                      Meinen Wert übernehmen
                    </Button>
                    <Button variant="secondary" className="text-xs px-3 py-1" onClick={() => entfernen(e.id)}>
                      Server-Wert behalten
                    </Button>
                  </div>
                </div>
              )}

              {e.status === "fehler" && (
                <div className="mt-2 space-y-2">
                  <div className="text-red-300 text-xs">Vom Server abgelehnt: {e.letzterFehler}</div>
                  <Button variant="secondary" className="text-xs px-3 py-1" onClick={() => entfernen(e.id)}>
                    Verwerfen
                  </Button>
                </div>
              )}
            </li>
          ))}
        </ul>
      </Modal>
    </>
  );
}
